import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider';
import Icon from '../AppIcon';
import UserAvatar from '../ui/UserAvatar';
import { isAdmin } from '../../utils/permissions';
import useTranslation from '../../hooks/useTranslation';

/**
 * Profil de l'utilisateur et son menu, EN BAS de la barre laterale (le nom du
 * fichier date de l'ancienne barre d'en-tete).
 *
 * Props:
 *   collapsed  boolean — barre repliee : avatar seul, menu ouvert a droite.
 *                        Sinon : avatar + nom + courriel, menu ouvert vers le haut.
 */
const GlobalHeader = ({ collapsed = false }) => {
  const navigate = useNavigate();
  const { user, userProfile, signOut, profileLoading } = useAuth();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const { t } = useTranslation();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setIsProfileOpen(false);
      }
    };
    // Echap ferme le menu et rend le curseur au bouton.
    const handleKey = (event) => {
      if (event?.key === 'Escape') {
        setIsProfileOpen(false);
        buttonRef?.current?.focus();
      }
    };

    if (isProfileOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKey);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [isProfileOpen]);

  const handleLogout = async () => {
    setIsProfileOpen(false);
    await signOut();
    navigate('/login', { replace: true });
  };

  const getDisplayName = () => {
    if (profileLoading) return t('common.loading');
    return userProfile?.full_name || userProfile?.email?.split('@')?.[0] || t('sidebar.userFallback');
  };

  const getDisplayEmail = () => {
    if (profileLoading) return '';
    return userProfile?.email || user?.email || '';
  };

  return (
    <div className="relative" ref={dropdownRef}>
          <button
            ref={buttonRef}
            type="button"
            aria-haspopup="menu"
            aria-expanded={isProfileOpen}
            title={collapsed ? getDisplayName() : undefined}
            onClick={() => setIsProfileOpen(!isProfileOpen)}
            className={`w-full flex items-center gap-2.5 py-2 rounded-lg transition-colors text-left hover:bg-[#1E2F44] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 ${collapsed ? 'justify-center px-0' : 'px-3'} ${isProfileOpen ? 'bg-[#1E2F44]' : ''}`}
          >
            <UserAvatar user={userProfile} size={32} className="flex-shrink-0" />
            {/* Name + email */}
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#E6EDF3] leading-tight truncate">{getDisplayName()}</p>
                <p className="text-[11px] text-[#9FB3C8] leading-tight truncate">{getDisplayEmail()}</p>
              </div>
            )}
          </button>

          {isProfileOpen && (
            <div
              role="menu"
              className={`absolute w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50 ${collapsed ? 'left-full bottom-0 ml-2' : 'left-0 bottom-full mb-1.5'}`}
            >
              <div className="px-3 py-2 border-b border-gray-200">
                <p className="text-xs font-medium text-[#2C3E50] leading-tight truncate">{getDisplayName()}</p>
                <p className="text-[11px] text-[#6B7280] leading-tight mt-0.5 truncate">{getDisplayEmail()}</p>
              </div>
              <div className="p-1">
                <Link
                  to="/profile"
                  onClick={() => setIsProfileOpen(false)}
                  className="flex items-center space-x-2.5 px-2.5 py-1.5 rounded-md hover:bg-gray-50 transition-colors text-left w-full"
                >
                  <Icon name="User" size={15} className="text-[#6B7280]" />
                  <span className="text-xs text-[#2C3E50]">{t('userManagement.header.myProfile')}</span>
                </Link>
                {isAdmin(userProfile) && (
                  <Link
                    to="/users"
                    onClick={() => setIsProfileOpen(false)}
                    className="flex items-center space-x-2.5 px-2.5 py-1.5 rounded-md hover:bg-gray-50 transition-colors text-left w-full"
                  >
                    <Icon name="Users" size={15} className="text-[#6B7280]" />
                    <span className="text-xs text-[#2C3E50]">{t('userManagement.header.manageUsers')}</span>
                  </Link>
                )}
                {isAdmin(userProfile) && (
                  <Link
                    to="/org-settings"
                    onClick={() => setIsProfileOpen(false)}
                    className="flex items-center space-x-2.5 px-2.5 py-1.5 rounded-md hover:bg-gray-50 transition-colors text-left w-full"
                  >
                    <Icon name="Settings2" size={15} className="text-[#6B7280]" />
                    <span className="text-xs text-[#2C3E50]">{t('orgSettings.navLabel')}</span>
                  </Link>
                )}
                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-2.5 px-2.5 py-1.5 rounded-md hover:bg-red-50 transition-colors text-left w-full"
                >
                  <Icon name="LogOut" size={15} className="text-red-600" />
                  <span className="text-xs text-red-600">{t('userManagement.header.logout')}</span>
                </button>
              </div>
            </div>
          )}
        </div>
  );
};

export default GlobalHeader;
